import type {
  BackgroundAdjustments,
  DeviceShadowAdjustments,
  DiffuseAdjustments,
  LoadedMockupAssets,
  ReflectionAdjustments,
  ScreenOverlayAdjustments
} from './composite';
import { renderMaster } from './composite';
import { StaticRenderCache } from './StaticRenderCache';
import {
  createStaticBackgroundSequenceRenderer,
  type StaticBackgroundComposition,
  type StaticBackgroundSequenceRenderer
} from './staticBackground';
import { getStaticExportSize, renderStaticExport } from './export';
import { normalizeBackgroundMotion, type DynamicZoomViewport, type DynamicZoomBackgroundMotion } from './dynamicZoom';
import { getFramedCameraRect } from './staticFraming';
import { seekVideo } from './loadImage';
import { EncoderBackpressureGate } from './EncoderBackpressureGate';
import { UserVideoDecoderPipeline } from './UserVideoDecoderPipeline';
import {
  getRenderAbortError,
  RenderStallWatchdog,
  throwIfRenderAborted,
  waitForRenderOperation,
  yieldToRenderControls
} from './RenderTask';
import { StreamingMp4Muxer } from './streamingMp4Muxer';
import {
  prepareFirstUserAudioTrack,
  type PreparedUserAudioTrack
} from './userAudioTrack';
import {
  createMemoryVideoExportArtifact,
  type VideoExportArtifact
} from './videoExportStorage';
import type {
  CustomPlacement,
  ExportResolution,
  SafeRect,
  ScreenWarpMode,
  StaticExportFormat,
  StaticExportMode
} from '../types';

const DEFAULT_STATIC_VIDEO_FPS = 30;
const DEFAULT_STATIC_VIDEO_GOP = 30;
const STATIC_VIDEO_MAX_SCREEN_LAYER_SIZE = 3200;
const STATIC_VIDEO_WIDE_MAX_SCREEN_LAYER_SIZE = 3840;

export interface StaticVideoExportOptions {
  format: StaticExportFormat;
  resolution: ExportResolution;
  exportMode: StaticExportMode;
  safeRect: SafeRect;
  renderSize: number;
  preserveAlpha?: boolean;
  chromaKeyColor?: string;
  diffuse?: DiffuseAdjustments;
  background?: BackgroundAdjustments;
  staticBackground?: StaticBackgroundComposition & { source: CanvasImageSource };
  animatedStaticBackground?: {
    durationSeconds: number;
    compositionAtTime: (timeSeconds: number) => StaticBackgroundComposition;
  };
  /** Dynamic Zoom: a window travelling over the finished frame. */
  dynamicCamera?: {
    backgroundMotion?: DynamicZoomBackgroundMotion;
    durationSeconds: number;
    viewportAtTime: (timeSeconds: number) => DynamicZoomViewport;
    /** Tightest zoom anywhere in the clip; sets how much detail the master needs. */
    maxZoom: number;
  };
  /**
   * Largest frame the template's own artwork can fill, in pixels. The composed
   * frame is never built beyond it, because past that point there is nothing
   * left to reveal — only the same pixels, larger.
   */
  sourceCeiling?: number;
  deviceShadow?: DeviceShadowAdjustments;
  reflection?: ReflectionAdjustments;
  screenOverlay?: ScreenOverlayAdjustments;
  useAlternateMask?: boolean;
  customPlacement?: CustomPlacement | null;
  customPlacement2?: CustomPlacement | null;
  customPlacement3?: CustomPlacement | null;
  warpMode?: ScreenWarpMode;
  warpMode2?: ScreenWarpMode;
  warpMode3?: ScreenWarpMode;
  userSource2?: CanvasImageSource | null;
  userSource3?: CanvasImageSource | null;
  onProgress?: (completedFrames: number, totalFrames: number) => void;
  onStageChange?: (stage: 'rendering' | 'packaging') => void;
  outputFileHandle?: FileSystemFileHandle | null;
  userSourceUrl?: string;
  userSourceUrl2?: string;
  userSourceUrl3?: string;
  /** Triple Screen exports intentionally preserve audio from Screen 1 only. */
  usePrimarySourceAudioOnly?: boolean;
  includeSourceAudio?: boolean;
  signal?: AbortSignal;
}

/**
 * How large the composed frame has to be for the camera to stay honest.
 *
 * A zoom is a crop, so a frame built at the export size has to magnify what it
 * cut: at 2x a 1920 master yields 960 real pixels stretched back over 1920.
 * Building the master `maxZoom` times larger makes that same cut land at 1:1 —
 * the export stops being a scaled-up 2K and becomes a 2K that was always 2K.
 *
 * The artwork sets the limit. A 2K export zooming 2x asks for 3840, which the
 * 3840px templates supply exactly; a 4K export zooming 2x would ask for 7680,
 * which nothing can supply, so it stays at 3840 and accepts the magnification
 * that the source itself makes unavoidable.
 */
export function getCameraMasterSize(
  renderSize: number,
  maxZoom: number,
  sourceCeiling: number | undefined
): number {
  const ceiling = Math.max(renderSize, Math.round(sourceCeiling || 0));
  const wanted = Math.round(renderSize * Math.max(1, maxZoom));
  return Math.min(wanted, ceiling);
}

export function isStaticVideoExportSupported(): boolean {
  return isStaticVideoWebCodecsSupported() || getStaticVideoRecorderMimeType() !== null;
}

export function isStaticVideoWebCodecsSupported(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
}

function getStaticVideoRecorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return null;
  }

  const candidates = [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4;codecs=h264',
    'video/mp4'
  ];

  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? null;
}

function getStaticVideoExportBitrate(width: number, height: number): number {
  const megapixels = (width * height) / 1_000_000;
  return Math.max(12_000_000, Math.round(megapixels * 12_000_000));
}

function getStaticVideoScreenRenderScale(renderSize: number, format: StaticExportFormat): number {
  const maxLayerSize =
    format === '16:9' ? STATIC_VIDEO_WIDE_MAX_SCREEN_LAYER_SIZE : STATIC_VIDEO_MAX_SCREEN_LAYER_SIZE;
  return Math.max(1, Math.min(2, maxLayerSize / Math.max(1, renderSize)));
}

async function findSupportedStaticVideoEncoderConfig(
  baseConfig: VideoEncoderConfig
): Promise<VideoEncoderConfig> {
  if (typeof VideoEncoder.isConfigSupported !== 'function') {
    return baseConfig;
  }

  const codecCandidates = ['avc1.4d0028', 'avc1.42C028', 'avc1.4d0033'];
  const hardwareCandidates: Array<VideoEncoderConfig['hardwareAcceleration']> = [
    'prefer-hardware',
    'no-preference'
  ];

  for (const hardwareAcceleration of hardwareCandidates) {
    for (const codec of codecCandidates) {
      const config: VideoEncoderConfig = { ...baseConfig, codec, hardwareAcceleration };
      try {
        const support = await VideoEncoder.isConfigSupported(config);
        if (support.supported) {
          return config;
        }
      } catch {
        // Some browsers throw instead of returning supported:false.
      }
    }
  }

  throw new Error('MP4 rendering is unavailable in this browser.');
}

export async function renderStaticVideoExport(
  assets: LoadedMockupAssets,
  userSourceInput: CanvasImageSource | null,
  options: StaticVideoExportOptions
): Promise<VideoExportArtifact> {
  throwIfRenderAborted(options.signal);
  if (!isStaticVideoExportSupported()) {
    throw new Error('MP4 rendering is unavailable in this browser.');
  }
  if (!isStaticVideoWebCodecsSupported()) {
    if (options.animatedStaticBackground) {
      throw new Error('Animated background MP4 rendering requires Google Chrome on Mac/PC.');
    }
    if (
      !(userSourceInput instanceof HTMLVideoElement) ||
      options.userSource2 instanceof HTMLVideoElement ||
      options.userSource3 instanceof HTMLVideoElement
    ) {
      throw new Error('Multi-screen MP4 rendering requires Google Chrome on Mac/PC.');
    }
    const blob = await renderStaticVideoExportBlobWithRecorder(assets, userSourceInput, options);
    return createMemoryVideoExportArtifact(blob);
  }

  const videos = [userSourceInput, options.userSource2, options.userSource3].filter(
    (source): source is HTMLVideoElement => source instanceof HTMLVideoElement
  );
  const videoDuration = Math.max(0, ...videos.map((video) => video.duration));
  const animationDuration = options.animatedStaticBackground?.durationSeconds ?? 0;
  const cameraDuration = options.dynamicCamera?.durationSeconds ?? 0;
  // A clip sets the length when there is one; otherwise whichever timed effect
  // is running does, since a still has no length of its own.
  const duration = videoDuration > 0 ? videoDuration : Math.max(animationDuration, cameraDuration);
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error('Unable to read video duration.');
  }

  const fps = DEFAULT_STATIC_VIDEO_FPS;
  const totalFrames = Math.max(1, Math.ceil(duration * fps));
  const outputSize = getStaticExportSize(options.format, options.resolution);
  const outputWidth = outputSize.width;
  const outputHeight = outputSize.height;
  // One size for the whole clip, not one per frame: a master that grew and
  // shrank with the zoom would make the picture's sharpness visibly breathe.
  const masterSize = options.dynamicCamera
    ? getCameraMasterSize(options.renderSize, options.dynamicCamera.maxZoom, options.sourceCeiling)
    : options.renderSize;
  const screenRenderScale = getStaticVideoScreenRenderScale(masterSize, options.format);
  const bitrate = getStaticVideoExportBitrate(outputWidth, outputHeight);
  // The camera changes only the final crop, not the composed scene. Keep its
  // full-resolution master for this export only; either video screen or an
  // animated background must still be composed for every frame.
  const reuseCameraMaster = Boolean(options.dynamicCamera)
    && !assets.template.isVideo
    && videos.length === 0
    && !options.animatedStaticBackground
    && !(options.staticBackground?.source instanceof HTMLVideoElement);
  let cameraMaster: HTMLCanvasElement | null = null;
  let outputCanvas: HTMLCanvasElement | null = null;
  // Camera-only stills already retain the complete scene. Live footage and
  // animated backgrounds prepare only immutable layers/masks, never the frame.
  const staticCache = !assets.template.isVideo && !reuseCameraMaster ? new StaticRenderCache() : undefined;

  const encoderConfig = await waitForRenderOperation(
    findSupportedStaticVideoEncoderConfig({
      codec: 'avc1.4d0028',
      width: outputWidth,
      height: outputHeight,
      bitrate,
      framerate: fps,
      hardwareAcceleration: 'prefer-hardware',
      avc: {
        format: 'avc'
      }
    }),
    {
      signal: options.signal,
      label: 'Video encoder capability check'
    }
  );

  let encoderGate: EncoderBackpressureGate | null = null;
  let muxer: StreamingMp4Muxer | null = null;
  let userPipeline: UserVideoDecoderPipeline | null = null;
  let userPipeline2: UserVideoDecoderPipeline | null = null;
  let userPipeline3: UserVideoDecoderPipeline | null = null;
  let userAudio: PreparedUserAudioTrack | null = null;
  let audioWriteResult: Promise<Error | null> | null = null;
  let animatedBackgroundRenderer: StaticBackgroundSequenceRenderer | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      try {
        if (!muxer) {
          throw new Error('MP4 streaming output is not initialized.');
        }
        muxer.enqueueVideoChunk(chunk, meta);
        encoderGate?.notifyOutput();
      } catch (error) {
        encoderGate?.fail(error);
      }
    },
    error: (error) => {
      encoderGate?.fail(error);
    }
  });
  encoderGate = new EncoderBackpressureGate(() => encoder.encodeQueueSize);
  const handleAbort = () => {
    encoderGate?.fail(getRenderAbortError(options.signal));
  };
  options.signal?.addEventListener('abort', handleAbort, { once: true });

  try {
    if (options.animatedStaticBackground) {
      const rendererPromise = createStaticBackgroundSequenceRenderer(
        options.animatedStaticBackground.compositionAtTime(0),
        masterSize
      );
      try {
        animatedBackgroundRenderer = await waitForRenderOperation(rendererPromise, {
          signal: options.signal,
          label: 'Animated background initialization'
        });
      } catch (error) {
        void rendererPromise.then((renderer) => renderer.dispose()).catch(() => undefined);
        throw error;
      }
    }
    if (userSourceInput instanceof HTMLVideoElement && options.userSourceUrl) {
      const candidatePipeline = new UserVideoDecoderPipeline(
        options.userSourceUrl,
        options.signal
      );
      try {
        await candidatePipeline.initialize();
        userPipeline = candidatePipeline;
      } catch (error) {
        candidatePipeline.close();
        throwIfRenderAborted(options.signal);
        console.warn(
          '[StaticVideoExport] Sequential decoder unavailable; using HTML video seek fallback.',
          error
        );
      }
    }
    if (options.userSource2 instanceof HTMLVideoElement && options.userSourceUrl2) {
      const candidatePipeline = new UserVideoDecoderPipeline(options.userSourceUrl2, options.signal);
      try {
        await candidatePipeline.initialize();
        userPipeline2 = candidatePipeline;
      } catch (error) {
        candidatePipeline.close();
        throwIfRenderAborted(options.signal);
        console.warn('[StaticVideoExport] Screen 2 sequential decoder unavailable; using HTML video seek fallback.', error);
      }
    }
    if (options.userSource3 instanceof HTMLVideoElement && options.userSourceUrl3) {
      const candidatePipeline = new UserVideoDecoderPipeline(options.userSourceUrl3, options.signal);
      try {
        await candidatePipeline.initialize();
        userPipeline3 = candidatePipeline;
      } catch (error) {
        candidatePipeline.close();
        throwIfRenderAborted(options.signal);
        console.warn('[StaticVideoExport] Screen 3 sequential decoder unavailable; using HTML video seek fallback.', error);
      }
    }

    userAudio = await prepareFirstUserAudioTrack(
      options.usePrimarySourceAudioOnly
        ? [options.userSourceUrl]
        : [options.userSourceUrl, options.userSourceUrl2, options.userSourceUrl3],
      duration,
      options.signal,
      options.includeSourceAudio !== false
    );

    muxer = await StreamingMp4Muxer.create({
      totalFrames,
      frameRate: fps,
      bitrate,
      durationSeconds: duration,
      outputFileHandle: options.outputFileHandle,
      signal: options.signal,
      audio: userAudio
        ? {
            codec: userAudio.codec,
            maximumPacketCount: userAudio.maximumPacketCount,
            estimatedBytes: userAudio.estimatedBytes
          }
        : undefined,
      onError: (error) => encoderGate?.fail(error)
    });
    if (userAudio) {
      audioWriteResult = userAudio
        .writeTo((packet, metadata) => muxer!.addAudioPacket(packet, metadata), options.signal)
        .then(
          () => null,
          (error) => {
            const normalized = error instanceof Error ? error : new Error(String(error));
            encoderGate?.fail(normalized);
            return normalized;
          }
        );
    }
    encoderGate.throwIfUnavailable(options.signal);
    encoder.configure(encoderConfig);
    options.onStageChange?.('rendering');
    videos.forEach((video) => video.pause());

    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
      encoderGate.throwIfUnavailable(options.signal);
      const time = Math.min(duration - 1 / 120, frameIndex / fps);
      let decodedUserFrame: VideoFrame | null = null;
      let decodedUserFrame2: VideoFrame | null = null;
      let decodedUserFrame3: VideoFrame | null = null;
      let encodedFrame: VideoFrame | null = null;
      try {
        const primaryTime = userSourceInput instanceof HTMLVideoElement
          ? Math.min(Math.max(0, userSourceInput.duration - 1 / 120), Math.max(0, time))
          : 0;
        const secondaryTime = options.userSource2 instanceof HTMLVideoElement
          ? Math.min(Math.max(0, options.userSource2.duration - 1 / 120), Math.max(0, time))
          : 0;
        const tertiaryTime = options.userSource3 instanceof HTMLVideoElement
          ? Math.min(Math.max(0, options.userSource3.duration - 1 / 120), Math.max(0, time))
          : 0;
        if (userPipeline) {
          decodedUserFrame = await userPipeline.seekToTime(primaryTime);
        }
        if (userSourceInput instanceof HTMLVideoElement && !decodedUserFrame) {
          await seekVideo(userSourceInput, primaryTime, { signal: options.signal });
        }
        if (userPipeline2) {
          decodedUserFrame2 = await userPipeline2.seekToTime(secondaryTime);
        }
        if (options.userSource2 instanceof HTMLVideoElement && !decodedUserFrame2) {
          await seekVideo(options.userSource2, secondaryTime, { signal: options.signal });
        }
        if (userPipeline3) {
          decodedUserFrame3 = await userPipeline3.seekToTime(tertiaryTime);
        }
        if (options.userSource3 instanceof HTMLVideoElement && !decodedUserFrame3) {
          await seekVideo(options.userSource3, tertiaryTime, { signal: options.signal });
        }
        const userSource: CanvasImageSource | null = decodedUserFrame ?? userSourceInput;
        const userSource2: CanvasImageSource | null = decodedUserFrame2 ?? options.userSource2 ?? null;
        const userSource3: CanvasImageSource | null = decodedUserFrame3 ?? options.userSource3 ?? null;
        let staticBackground = options.staticBackground;
        if (options.animatedStaticBackground && animatedBackgroundRenderer) {
          const composition = options.animatedStaticBackground.compositionAtTime(time);
          staticBackground = {
            ...composition,
            source: await waitForRenderOperation(
              animatedBackgroundRenderer.render(composition),
              {
                signal: options.signal,
                label: `Animated background frame ${frameIndex + 1}`
              }
            )
          };
        }
        const master = cameraMaster ?? renderMaster(assets, userSource, {
          userImage2: userSource2,
          userImage3: userSource3,
          renderSize: masterSize,
          gridX: 32,
          gridY: 32,
          diffuse: options.diffuse,
          background: options.background,
          staticBackground,
          separateCameraBackground: Boolean(options.dynamicCamera) && normalizeBackgroundMotion(options.dynamicCamera?.backgroundMotion) !== 'stick',
          deviceShadow: options.deviceShadow,
          reflection: options.reflection,
          screenOverlay: options.screenOverlay,
          useAlternateMask: options.useAlternateMask,
          customPlacement: options.customPlacement,
          customPlacement2: options.customPlacement2,
          customPlacement3: options.customPlacement3,
          warpMode: options.warpMode,
          warpMode2: options.warpMode2,
          warpMode3: options.warpMode3,
          screenRenderScale,
          forExport: true,
          staticCache
        });
        if (reuseCameraMaster) cameraMaster = master;

        // Resolve timing once; both layers use the same camera sample.
        // The final encoded frame starts before duration. Sample the camera's
        // endpoint there so the delivered clip visibly finishes its last move.
        // Footage/audio timestamps and frame count remain unchanged.
        const cameraTime = frameIndex === totalFrames - 1 ? duration : time;
        const cameraViewport = options.dynamicCamera?.viewportAtTime(cameraTime);
        const sourceRect = cameraViewport
          ? getFramedCameraRect(cameraViewport, master.width, master.height, {
              format: options.format, safeRect: options.safeRect, referenceSize: options.renderSize
            })
          : undefined;

        const canvas = renderStaticExport(
          master,
          options.format,
          options.resolution,
          options.exportMode,
          options.safeRect,
          {
            preserveAlpha: options.preserveAlpha,
            matteColor: options.chromaKeyColor,
            sourceRect,
            dynamicCamera: options.dynamicCamera && normalizeBackgroundMotion(options.dynamicCamera.backgroundMotion) !== 'stick' ? {
              viewport: cameraViewport!,
              framing: { format: options.format, safeRect: options.safeRect, referenceSize: options.renderSize },
              backgroundMotion: options.dynamicCamera.backgroundMotion
            } : undefined,
            outputCanvas: outputCanvas ?? undefined
          }
        );
        outputCanvas = canvas;

        encodedFrame = new VideoFrame(canvas, {
          timestamp: Math.round((frameIndex * 1_000_000) / fps),
          duration: Math.round(1_000_000 / fps)
        });
        encoder.encode(encodedFrame, {
          keyFrame: frameIndex % DEFAULT_STATIC_VIDEO_GOP === 0
        });
      } finally {
        encodedFrame?.close();
        decodedUserFrame?.close();
        decodedUserFrame2?.close();
        decodedUserFrame3?.close();
      }
      await Promise.all([
        encoderGate.waitForCapacity(options.signal),
        muxer.waitForCapacity()
      ]);

      options.onProgress?.(frameIndex + 1, totalFrames);
      if (frameIndex % 2 === 1) {
        await yieldToRenderControls(options.signal);
      }
    }

    options.onStageChange?.('packaging');
    await encoderGate.waitForOperation(encoder.flush(), options.signal, 'Video packaging');
    encoderGate.throwIfUnavailable(options.signal);
    if (audioWriteResult) {
      const audioError = await audioWriteResult;
      if (audioError) {
        throw audioError;
      }
    }
    return await muxer.finalize();
  } finally {
    options.signal?.removeEventListener('abort', handleAbort);
    if (encoder.state !== 'closed') {
      encoder.close();
    }
    userPipeline?.close();
    userPipeline2?.close();
    userPipeline3?.close();
    userAudio?.dispose();
    animatedBackgroundRenderer?.dispose();
    staticCache?.dispose();
    // Release the retained surfaces on success, cancellation and failure.
    // VideoFrame captures its own snapshot before these canvases are reused.
    if (cameraMaster) {
      cameraMaster.width = 1;
      cameraMaster.height = 1;
    }
    if (outputCanvas) {
      outputCanvas.width = 1;
      outputCanvas.height = 1;
    }
    await audioWriteResult;
    await muxer?.cancel();
  }
}

async function renderStaticVideoExportBlobWithRecorder(
  assets: LoadedMockupAssets,
  userVideo: HTMLVideoElement,
  options: StaticVideoExportOptions
): Promise<Blob> {
  const mimeType = getStaticVideoRecorderMimeType();
  if (!mimeType) {
    throw new Error('MP4 rendering is unavailable in this browser.');
  }

  const duration = userVideo.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error('Unable to read video duration.');
  }

  const fps = DEFAULT_STATIC_VIDEO_FPS;
  const totalFrames = Math.max(1, Math.ceil(duration * fps));
  const outputSize = getStaticExportSize(options.format, options.resolution);
  const masterSize = options.dynamicCamera
    ? getCameraMasterSize(options.renderSize, options.dynamicCamera.maxZoom, options.sourceCeiling)
    : options.renderSize;
  const screenRenderScale = getStaticVideoScreenRenderScale(masterSize, options.format);
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = outputSize.width;
  outputCanvas.height = outputSize.height;
  const outputContext = outputCanvas.getContext('2d');
  if (!outputContext) {
    throw new Error('2D context is not available for MP4 recorder canvas.');
  }

  if (typeof outputCanvas.captureStream !== 'function') {
    throw new Error('MP4 rendering is unavailable in this browser.');
  }

  const stream = outputCanvas.captureStream(fps);
  const chunks: Blob[] = [];
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: getStaticVideoExportBitrate(outputSize.width, outputSize.height)
    });
  } catch (error) {
    stopMediaStream(stream);
    throw error;
  }

  const drawFrame = (cameraTime = userVideo.currentTime): void => {
    const master = renderMaster(assets, userVideo, {
      userImage2: options.userSource2,
      userImage3: options.userSource3,
      renderSize: masterSize,
      gridX: 24,
      gridY: 24,
      diffuse: options.diffuse,
      background: options.background,
      staticBackground: options.staticBackground,
      separateCameraBackground: Boolean(options.dynamicCamera) && normalizeBackgroundMotion(options.dynamicCamera?.backgroundMotion) !== 'stick',
      deviceShadow: options.deviceShadow,
      reflection: options.reflection,
      screenOverlay: options.screenOverlay,
      useAlternateMask: options.useAlternateMask,
      customPlacement: options.customPlacement,
      customPlacement2: options.customPlacement2,
      customPlacement3: options.customPlacement3,
      warpMode: options.warpMode,
      warpMode2: options.warpMode2,
      warpMode3: options.warpMode3,
      screenRenderScale,
      forExport: true,
      realtime: false
    });
    const exported = renderStaticExport(
      master,
      options.format,
      options.resolution,
      options.exportMode,
      options.safeRect,
      {
        preserveAlpha: options.preserveAlpha,
        matteColor: options.chromaKeyColor,
        dynamicCamera: options.dynamicCamera ? {
          viewport: options.dynamicCamera.viewportAtTime(cameraTime),
          framing: { format: options.format, safeRect: options.safeRect, referenceSize: options.renderSize },
          backgroundMotion: options.dynamicCamera.backgroundMotion
        } : undefined
      }
    );
    outputContext.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
    outputContext.drawImage(exported, 0, 0);
  };

  let failRun: (error: Error) => void = () => {};
  let runFailed = false;
  const runFailure = new Promise<never>((_, reject) => {
    failRun = (error: Error) => {
      if (runFailed) {
        return;
      }
      runFailed = true;
      reject(error);
    };
  });
  const recordingDone = new Promise<Blob>((resolve) => {
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    });
    recorder.addEventListener('stop', () => {
      resolve(new Blob(chunks, { type: mimeType }));
    }, { once: true });
  });
  const onRecorderError = () => failRun(new Error('MP4 recording failed.'));
  recorder.addEventListener('error', onRecorderError);

  let playbackFinished = false;
  let frameRequestId: number | null = null;
  let lastCompletedFrames = 0;
  let resolvePlayback: () => void = () => {};
  const playbackDone = new Promise<void>((resolve) => {
    resolvePlayback = resolve;
  });
  const playbackWatchdog = new RenderStallWatchdog(() => {
    failRun(new Error('Video playback stopped responding during MP4 rendering.'));
  });
  const onAbort = () => failRun(getRenderAbortError(options.signal));
  const stopRecorder = (): void => {
    if (recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // The recorder may already be stopping after an error.
      }
    }
  };
  const finishPlayback = (): void => {
    if (playbackFinished) {
      return;
    }
    playbackFinished = true;
    try {
      drawFrame(duration);
      userVideo.pause();
      options.onProgress?.(totalFrames, totalFrames);
      playbackWatchdog.touch();
      stopRecorder();
      resolvePlayback();
    } catch (error) {
      failRun(error instanceof Error ? error : new Error(String(error)));
    }
  };
  const tick = (): void => {
    if (playbackFinished || runFailed) {
      return;
    }
    try {
      drawFrame();
      const completedFrames = Math.max(1, Math.min(totalFrames, Math.round(userVideo.currentTime * fps)));
      options.onProgress?.(completedFrames, totalFrames);
      if (completedFrames > lastCompletedFrames) {
        lastCompletedFrames = completedFrames;
        playbackWatchdog.touch();
      }
      if (userVideo.ended || userVideo.currentTime >= duration - 1 / fps) {
        finishPlayback();
        return;
      }
      frameRequestId = window.requestAnimationFrame(tick);
    } catch (error) {
      failRun(error instanceof Error ? error : new Error(String(error)));
    }
  };

  try {
    options.onStageChange?.('rendering');
    userVideo.pause();
    await seekVideo(userVideo, 0, { signal: options.signal });
    throwIfRenderAborted(options.signal);
    options.signal?.addEventListener('abort', onAbort, { once: true });
    userVideo.loop = false;
    userVideo.muted = true;
    userVideo.playsInline = true;
    drawFrame();
    recorder.start(250);

    try {
      await waitForRenderOperation(
        Promise.race([userVideo.play(), runFailure]),
        {
          signal: options.signal,
          label: 'Video playback start'
        }
      );
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error('Unable to start video playback for MP4 rendering.');
    }

    userVideo.addEventListener('ended', finishPlayback, { once: true });
    frameRequestId = window.requestAnimationFrame(tick);
    await Promise.race([playbackDone, runFailure]);

    options.onStageChange?.('packaging');
    const blob = await waitForRenderOperation(
      Promise.race([recordingDone, runFailure]),
      {
        signal: options.signal,
        label: 'MP4 recorder finalization'
      }
    );
    if (blob.size === 0) {
      throw new Error('MP4 recorder produced an empty file.');
    }
    return blob;
  } finally {
    playbackFinished = true;
    playbackWatchdog.stop();
    options.signal?.removeEventListener('abort', onAbort);
    userVideo.removeEventListener('ended', finishPlayback);
    recorder.removeEventListener('error', onRecorderError);
    if (frameRequestId !== null) {
      window.cancelAnimationFrame(frameRequestId);
    }
    userVideo.pause();
    stopRecorder();
    stopMediaStream(stream);
  }
}

function stopMediaStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}
